import { applyDecoratedDescriptor as _applyDecoratedDescriptor } from './_virtual/_rollupPluginBabelHelpers.mjs';
import _en from './translations/en.json.mjs';
import React from 'react';
import { Buffer } from 'buffer';
import { Text, Labelled, EventListener, rgbToHsb } from '@shopify/polaris';
import { classNames } from '@shopify/css-utilities';
import { autobind } from './packages/@web-utilities/decorators/index.mjs';
import { withI18n } from '@shopify/react-i18n';
import compose from '@shopify/react-compose';
import { LinkSelectionType, ToolbarElement, RteModal, Alignment, LinkTarget, RteCommand, TypographicElement } from './types/index.mjs';
export { ToolbarElement } from './types/index.mjs';
import styles from './RichTextEditor.scss.mjs';
import { contentStyles } from './RichTextEditorContentStyles.mjs';
import { withError } from './packages/@web-utilities/error/decorators.mjs';
import Toolbar from './components/Toolbar/index.mjs';
import { sanitize, unsanitize } from './packages/@shopify-extension/security/sanitize-html/sanitize-html.mjs';
import InsertVideoModal from './components/InsertVideoModal/InsertVideoModal.mjs';
import InsertLinkModal from './components/InsertLinkModal/InsertLinkModal.mjs';
import EditImageModal from './components/EditImageModal/EditImageModal.mjs';
import { isTinyMCEBookmarkElement } from './utilities/is-tinymce-bookmark.mjs';
import { memoize } from 'lodash-decorators';

var _dec, _class;

/* eslint-disable @typescript-eslint/naming-convention */
const TINYMCE_CONFIG = {
  plugins: 'table,autoresize,paste,noparsing,modeoverride,copypaste',
  autoresize_overflow_padding: 0,
  autoresize_bottom_margin: 0,
  autoresize_max_height: 500,
  autoresize_on_init: true,
  element_format: 'html',
  remove_trailing_brs: false
};
/* eslint-enable @typescript-eslint/naming-convention */
const MIN_HEIGHT = 150;
const DEFAULT_BLACK = {
  red: 0,
  green: 0,
  blue: 0
};
let RichTextEditor = (_dec = memoize((command, value) => `${command}${value}`), (_class = class RichTextEditor extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      showHTML: false,
      focused: false,
      tableSelected: false,
      imageSelected: false,
      selectedTypography: {
        bold: false,
        italic: false,
        underline: false,
        orderedList: false,
        unorderedList: false
      },
      selectedTypographicElement: undefined,
      linkSelectionType: LinkSelectionType.Empty,
      popoverVisible: undefined,
      overflowVisible: false,
      htmlStyles: {
        display: 'none'
      },
      codeMirrorLoaded: false,
      tinyMceLoaded: false,
      editorHeight: MIN_HEIGHT
    };
    this.container = /*#__PURE__*/React.createRef();
    this.textarea = /*#__PURE__*/React.createRef();
    this.editor = null;
    this.htmlSerializer = null;
  }
  async componentDidMount() {
    await this.initializeTinyMCE();
    if (this.props.showHtmlInitially) {
      this.toggleHTML();
    }
  }
  componentDidUpdate({
    value: oldValue
  }) {
    this.setEditorVisibility();
    const {
      editor,
      textarea,
      props: {
        value
      }
    } = this;

    // TinyMCE holds its own value in memory which is the value that is rendered.
    // This value can drift from the value passed in from props if the prop value
    // is changed in the parent (ex: discard changes). In these cases we have to
    // force value synchronization.
    if (editor && textarea.current && value !== oldValue) {
      const editorContent = editor.getContent();
      if (editorContent !== value) {
        editor.setContent(value);
      }
    }
  }
  render() {
    const {
      id,
      i18n,
      label,
      toolbarInclusions = [ToolbarElement.TextFormat, ToolbarElement.TextModifierBold, ToolbarElement.TextModifierItalic, ToolbarElement.TextModifierUnderline, ToolbarElement.TextModifierColor, ToolbarElement.TextAlignment, ToolbarElement.InsertLink, ToolbarElement.InsertImage, ToolbarElement.InsertVideo, ToolbarElement.InsertTable, ToolbarElement.UnorderedList, ToolbarElement.OrderedList, ToolbarElement.Outdent, ToolbarElement.Indent, ToolbarElement.ClearFormatting],
      value,
      readOnly = false,
      InsertImageModalComponent,
      CodeMirrorEditorComponent,
      error
    } = this.props;
    const {
      showHTML,
      focused,
      tableSelected,
      imageSelected,
      selectedLink,
      selectedImageAltText,
      selectedImageUrl,
      selectedImageAlignment,
      selectedImageWrapText,
      selectedImageSpacing,
      selectedTextColor,
      selectedBackgroundColor,
      selectedTypographicElement,
      linkSelectionType,
      modalVisible,
      htmlStyles,
      popoverVisible,
      overflowVisible,
      editorStyles,
      codeMirrorLoaded,
      tinyMceLoaded
    } = this.state;
    const {
      bold,
      italic,
      underline,
      orderedList,
      unorderedList
    } = this.state.selectedTypography;
    const textareaClassName = classNames(styles.Textarea, focused && styles['Textarea-focused'], readOnly && styles['Textarea-readonly']);
    const plainviewClassName = classNames(styles.PlainView, readOnly && styles['Textarea-readonly'], codeMirrorLoaded && styles['PlainView-hidden']);
    return /*#__PURE__*/React.createElement("div", {
      className: styles.RichTextEditor
    }, /*#__PURE__*/React.createElement(Text, {
      visuallyHidden: true,
      as: "span"
    }, i18n.translate('RichTextEditor.richTextEditor')), /*#__PURE__*/React.createElement(Labelled, {
      id: id,
      label: label
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.Editor
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.Toolbar
    }, /*#__PURE__*/React.createElement(Toolbar, {
      id: id,
      showHTML: showHTML,
      tableSelected: tableSelected,
      imageSelected: imageSelected,
      selectedBold: bold,
      selectedItalic: italic,
      selectedUnderline: underline,
      selectedUnorderedList: unorderedList,
      selectedOrderedList: orderedList,
      selectedTextColor: selectedTextColor,
      selectedBackgroundColor: selectedBackgroundColor,
      selectedTypographicElement: selectedTypographicElement,
      linkSelectionType: linkSelectionType,
      onNodeSelect: this.handleNodeSelect,
      onColorChange: this.handleColorChange,
      onToggleHTML: this.toggleHTML,
      executeCommand: this.handleExecuteCommand,
      openModal: this.handleOpenModal,
      popoverVisible: popoverVisible,
      onOpenPopover: this.handleOpenPopover,
      onClosePopover: this.handleClosePopover,
      overflowVisible: overflowVisible,
      onToggleOverflow: this.handleToggleOverflow,
      inclusions: toolbarInclusions,
      readOnly: readOnly || !tinyMceLoaded
    })), /*#__PURE__*/React.createElement("div", {
      className: textareaClassName,
      id: `richtexteditor_text_area-${id}`
    }, /*#__PURE__*/React.createElement("div", {
      ref: this.container,
      style: editorStyles
    }, /*#__PURE__*/React.createElement("div", null)), /*#__PURE__*/React.createElement("textarea", {
      ref: this.textarea,
      id: id,
      className: plainviewClassName,
      value: sanitize(value),
      onChange: this.handleTextareaValueChanged,
      onFocus: this.handleEditorFocus,
      onBlur: this.handleEditorBlur,
      disabled: readOnly
    }), showHTML && CodeMirrorEditorComponent && /*#__PURE__*/React.createElement(CodeMirrorEditorComponent, {
      style: htmlStyles,
      value: value,
      onChange: this.handleCodeMirrorChanged,
      onLoad: this.handleCodeMirrorLoaded,
      readOnly: readOnly
    }), error && /*#__PURE__*/React.createElement("span", {
      className: styles.InlineError
    }, /*#__PURE__*/React.createElement(Labelled, {
      label: label,
      labelHidden: true,
      id: id,
      error: error
    }))))), /*#__PURE__*/React.createElement(InsertImageModalComponent, {
      open: modalVisible === RteModal.InsertImage,
      onClose: this.closeModal,
      onInsertImage: this.handleInsertImage
    }), /*#__PURE__*/React.createElement(InsertVideoModal, {
      open: modalVisible === RteModal.InsertVideo,
      onClose: this.closeModal,
      onInsertVideo: this.handleInsertVideo
    }), /*#__PURE__*/React.createElement(InsertLinkModal, {
      open: modalVisible === RteModal.UpdateLink,
      selectedLink: selectedLink,
      onClose: this.closeModal,
      onUpdateLink: this.handleUpdateLink,
      onRemoveLink: this.handleRemoveLink
    }), /*#__PURE__*/React.createElement(EditImageModal, {
      open: modalVisible === RteModal.EditImage,
      altText: selectedImageAltText,
      imageUrl: selectedImageUrl,
      alignment: selectedImageAlignment,
      wrapText: selectedImageWrapText,
      spacing: selectedImageSpacing,
      onClose: this.closeModal,
      onEditImage: this.handleEditImage,
      onRemove: this.handleRemoveImage
    }), /*#__PURE__*/React.createElement(EventListener, {
      event: "click",
      handler: this.handleEditorBlur
    }));
  }
  componentWillUnmount() {
    if (this.editor) {
      this.editor.remove();
      this.editor = null;
    }
  }
  handleTextareaValueChanged({
    currentTarget: {
      value
    }
  }) {
    this.props.onChange(value);
  }
  handleCodeMirrorChanged(value) {
    this.props.onChange(value);
  }
  handleEditorValueChanged() {
    const {
      editor,
      textarea
    } = this;
    const {
      onChange
    } = this.props;
    if (!editor || !textarea || !textarea.current) {
      return;
    }
    const editorContent = editor.getContent();
    const {
      innerHTML: textareaHTML
    } = textarea.current;
    const unsanitizedTextareaValue = unsanitize(textareaHTML);
    if (this.formatHtml(editorContent) !== this.formatHtml(unsanitizedTextareaValue)) {
      onChange(editorContent);
    }
  }
  formatHtml(html) {
    if (!this.editor || !this.htmlSerializer) {
      return html;
    }

    // `<p></p>` will be formatted as `<p>&nbsp;<br></p>` by the parser to make empty paragraph selectables.
    // However, WYSIWYG editor will turn empty paragraphs into `<p> </p>` which will be formatted as <p>&nbsp;</p>.
    // To avoid triggering `onChange` on any key up, we need to clean empty paragraphs so formatted values can match.
    const nodes = this.editor.parser.parse(html.replace(/<p><\/p>/g, '<p>&nbsp;</p>'));
    return this.htmlSerializer.serialize(nodes);
  }
  async initializeTinyMCE() {
    const {
      default: tinymce
    } = await import( /* webpackChunkName: 'tinymce' */'@shopify/tinymce/web');
    await import( /* webpackChunkName: 'tinymce-plugins/Mode' */'./tinymce-plugins/Mode.mjs');
    await import( /* webpackChunkName: 'tinymce-plugins/CopyPaste' */'./tinymce-plugins/CopyPaste.mjs');
    const {
      textarea,
      container
    } = this;
    if (!textarea.current || !container.current) {
      return;
    }

    /* eslint-disable @typescript-eslint/naming-convention */
    await tinymce.init({
      ...tinymce.shopifyConfig,
      ...TINYMCE_CONFIG,
      selector: `#${this.props.id}`,
      theme: this.tinymceTheme,
      content_style: contentStyles,
      images_dataimg_filter: img => {
        if (img.src.startsWith('data:') && img.src.includes('base64')) {
          const [prefix, mimeType, encoding, data] = img.src.split(/[:;,]/);
          const decoded = Buffer.from(data, 'base64').toString();
          const encodedSanitized = `${prefix}:${mimeType};${encoding},${Buffer.from(sanitize(decoded).replace('data-sanitized-xmlns:', 'xmlns:'), 'utf8').toString('base64')}`;
          img.src = encodedSanitized;
        }
        return img.hasAttribute('internal-blob');
      }
    });
    /* eslint-enable @typescript-eslint/naming-convention */

    this.editor = tinymce.EditorManager.get(this.props.id);
    this.editor.on('blur', this.handleEditorBlur);
    this.editor.on('focus', this.handleEditorFocus);
    this.editor.on('keyup change', this.handleEditorValueChanged);
    this.editor.on('nodechange', this.handleSelectionChange);
    this.editor.on('click', this.handleClick);
    this.editor.on('dblclick', this.handleDoubleClick);
    this.editor.on('BeforeSetContent', this.sanitizeContent);
    this.editor.on('GetContent', this.unsanitizeContent);
    this.htmlSerializer = new tinymce.html.Serializer({
      /* eslint-disable @typescript-eslint/naming-convention */
      validate: true,
      entities: '160,nbsp',
      entity_encoding: 'named',
      element_format: 'html'
      /* eslint-enable @typescript-eslint/naming-convention */
    }, new tinymce.html.Schema({}));
    this.setInitialEditorValue();
    this.setEditorVisibility();
    this.handleTinyMceLoaded();
  }
  setInitialEditorValue() {
    const {
      value: productDescription,
      readOnly
    } = this.props;
    const initialValue = sanitize(productDescription);
    this.editor.setContent(initialValue);
    this.textarea.current.innerHTML = initialValue;
    if (readOnly) {
      this.editor.setMode('readonly');
    }
  }
  sanitizeContent(event) {
    if (isTinyMCEBookmarkElement(event.content)) return;
    event.content = sanitize(event.content);
  }
  unsanitizeContent(event) {
    event.content = unsanitize(event.content);
  }
  tinymceTheme() {
    const container = this.container;
    if (!container.current) {
      return;
    }
    return {
      editorContainer: container.current,
      iframeContainer: container.current.firstElementChild
    };
  }
  handleTinyMceLoaded() {
    this.setState({
      tinyMceLoaded: true
    });
  }
  handleCodeMirrorLoaded() {
    this.setState({
      codeMirrorLoaded: true
    });
  }
  setEditorVisibility() {
    var _container$current, _container$current$fi;
    const {
      editor,
      container
    } = this;
    const {
      readOnly
    } = this.props;
    const {
      codeMirrorLoaded,
      showHTML,
      editorHeight
    } = this.state;
    if (editor == null) {
      return;
    }
    const iframe = (_container$current = container.current) == null ? void 0 : (_container$current$fi = _container$current.firstElementChild) == null ? void 0 : _container$current$fi.firstElementChild;
    if (iframe) {
      iframe.style.minHeight = `${editorHeight}px`;
    }
    if (showHTML) {
      this.displayLegacyTextarea(!codeMirrorLoaded);
    } else {
      this.displayLegacyTextarea(false);
    }
    if (this.state.focused) {
      editor.focus(true);
    }
    if (readOnly) {
      editor.setMode('readonly');
      editor.getBody().classList.add('readonly');
    } else if (!showHTML) {
      editor.setMode('design', false);
      editor.getBody().classList.remove('readonly');
    }
  }
  displayLegacyTextarea(visible) {
    const {
      textarea
    } = this;
    if (!textarea.current) {
      return;
    }
    textarea.current.setAttribute('aria-hidden', visible ? 'false' : 'true');
    textarea.current.style.display = visible ? 'block' : 'none';
  }
  getCommandExecutor(command, value) {
    return () => {
      this.handleExecuteCommand(command, value);
    };
  }
  handleExecuteCommand(command, value, args) {
    if (this.editor) {
      this.editor.execCommand(command, false, value, args);
      if (this.state.overflowVisible) {
        this.setState({
          overflowVisible: false
        });
      }
    }
  }
  toggleHTML() {
    const {
      editor,
      props
    } = this;
    const {
      value: editorContents
    } = props;
    const {
      editorHeight: height
    } = this.state;
    if (!editor) {
      return;
    }
    const clientHeight = editor.getContentAreaContainer().clientHeight;
    if (clientHeight > MIN_HEIGHT) {
      this.setState({
        editorHeight: clientHeight
      });
    }
    this.setState(({
      showHTML
    }) => {
      const switchingToHtmlView = !showHTML;
      const switchingToRichTextView = showHTML;
      if (switchingToRichTextView) {
        sanitize(editorContents);
      }
      return {
        htmlStyles: switchingToHtmlView ? {
          height: `${editor.getContentAreaContainer().clientHeight}px`
        } : {
          display: 'none'
        },
        editorStyles: switchingToHtmlView ? {
          display: 'none'
        } : {
          minHeight: `${height}px`
        },
        showHTML: switchingToHtmlView,
        overflowVisible: false
      };
    });
    editor.focus(true);
  }
  handleInsertImage(image) {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    const img = editor.dom.createHTML('img', {
      src: image.src,
      alt: image.altText
    });
    this.handleExecuteCommand('mceInsertContent', img, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      skip_undo: 1
    });
    editor.undoManager.add();
    this.getCommandExecutor('mceRepaint')();
    this.getCommandExecutor('mceResize')();
    editor.focus(true);
    this.closeModal();
  }
  handleInsertVideo(videoCode) {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    this.getCommandExecutor('mceInsertRawHTML', videoCode)();
    this.closeModal();
  }
  handleSelectionChange() {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    const selection = editor.selection.getContent();
    const linkElement = this.getSelectedNodeOfType('A');
    const tableElement = this.getSelectedNodeOfType('TABLE');
    const imageElement = this.getSelectedNodeOfType('IMG');
    const divElement = this.getSelectedNodeOfType('div');
    const selectedTypographicElement = this.getSelectedTypographicElement();
    if (tableElement) {
      this.setState({
        tableSelected: true
      });
    } else {
      this.setState({
        tableSelected: false
      });
    }
    if (divElement) {
      const textAlign = window.getComputedStyle(divElement).textAlign;
      this.setState({
        selectedImageAlignment: textAlign
      });
    }
    if (imageElement) {
      const imageElementStyle = window.getComputedStyle(imageElement);
      const float = imageElementStyle.cssFloat;
      const newSpacing = calculateMargin(imageElementStyle);
      this.setState({
        imageSelected: true,
        selectedImageWrapText: float === Alignment.Left || float === Alignment.Right,
        selectedImageAltText: imageElement.getAttribute('alt') || '',
        selectedImageUrl: imageElement.getAttribute('src') || '',
        selectedImageSpacing: newSpacing
      });
    } else {
      this.setState({
        imageSelected: false
      });
    }
    if (linkElement) {
      const currentLink = {
        href: linkElement.getAttribute('href') || '',
        target: linkElement.getAttribute('target') || LinkTarget.Internal,
        title: linkElement.getAttribute('title') || ''
      };
      this.setState({
        linkSelectionType: LinkSelectionType.Edit,
        selectedLink: currentLink
      });
    } else if (selection) {
      this.setState({
        linkSelectionType: LinkSelectionType.Insert,
        selectedLink: undefined
      });
    } else {
      this.setState({
        linkSelectionType: LinkSelectionType.Empty
      });
    }
    if (selectedTypographicElement) {
      this.setState({
        selectedTypographicElement
      });
    }
  }
  handleEditorFocus() {
    if (this.props.onFocus) this.props.onFocus();
    this.setState({
      focused: true
    });
  }
  handleEditorBlur() {
    if (this.props.onBlur) this.props.onBlur();
    this.setState({
      focused: false
    });
  }
  handleOpenPopover(popover, overflow = false) {
    this.setState({
      popoverVisible: popover,
      overflowVisible: overflow
    });
  }
  handleClosePopover() {
    this.setState({
      popoverVisible: undefined
    });
  }
  handleCloseOverflow() {
    this.setState({
      overflowVisible: false
    });
  }
  handleToggleOverflow() {
    const {
      overflowVisible
    } = this.state;
    this.setState({
      overflowVisible: !overflowVisible
    });
  }
  getSelectedNodeOfType(tagName) {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    return editor.dom.getParent(editor.selection.getNode(), tagName);
  }
  handleUpdateLink(link) {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    editor.getDoc().execCommand('unlink', false);
    editor.execCommand('mceInsertLink', false, link, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      skip_undo: 1
    });
    editor.selection.collapse();
    this.handleSelectionChange();
    this.closeModal();
  }
  handleRemoveLink() {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    const linkElement = this.getSelectedNodeOfType('A');
    const bookmark = editor.selection.getBookmark();
    editor.dom.remove(linkElement, true);
    editor.selection.moveToBookmark(bookmark);
    this.closeModal();
    this.handleSelectionChange();
  }
  handleOpenModal(modal) {
    return () => this.setState({
      modalVisible: modal
    });
  }
  closeModal() {
    this.setState({
      modalVisible: undefined
    });
  }
  handleClick() {
    const spanElement = this.getSelectedNodeOfType('span');
    this.handleEditorFocus();
    this.handleNodeSelect(RteCommand.SelectNode);
    if (this.state.popoverVisible !== undefined) {
      this.handleClosePopover();
    }
    if (this.state.overflowVisible) {
      this.handleCloseOverflow();
    }
    if (spanElement) {
      this.handleColorState(spanElement);
    }
  }
  handleColorChange(target, color) {
    if (target === RteCommand.TextColor) {
      this.setState({
        selectedTextColor: color
      });
      return;
    }
    if (target === RteCommand.BackgroundColor) {
      this.setState({
        selectedBackgroundColor: color
      });
    }
  }
  handleColorState(node) {
    const textColor = window.getComputedStyle(node).color;
    const backgroundColor = window.getComputedStyle(node).backgroundColor;
    if (textColor != null) {
      this.setState({
        selectedTextColor: rgbToHsb(parseRgb(textColor))
      });
    }
    if (backgroundColor != null) {
      this.setState({
        selectedBackgroundColor: rgbToHsb(parseRgb(backgroundColor))
      });
    }
  }
  handleNodeSelect(command) {
    const buttonClick = command !== RteCommand.SelectNode;
    const boldNode = this.getSelectedNodeOfType('strong');
    const italicNode = this.getSelectedNodeOfType('em');
    const underlineNode = this.getSelectedNodeOfType('span');
    const orderedListNode = this.getSelectedNodeOfType('ol');
    const unorderedListNode = this.getSelectedNodeOfType('ul');
    const selectedTypographicElement = this.getSelectedTypographicElement();
    this.setState(({
      selectedTypography
    }) => {
      const {
        bold,
        italic,
        underline,
        orderedList,
        unorderedList
      } = selectedTypography;
      const [boldCmd, italicCmd, underlineCmd, orderedListCmd, unorderedListCmd] = [command === RteCommand.Bold, command === RteCommand.Italic, command === RteCommand.Underline, command === RteCommand.OrderedList, command === RteCommand.UnorderedList];
      const boldState = boldCmd ? !bold : bold;
      const italicState = italicCmd ? !italic : italic;
      const underlineState = underlineCmd ? !underline : underline;
      const olState = orderedListCmd ? !orderedList : orderedList;
      const ulState = unorderedListCmd ? !unorderedList : unorderedList;
      const nodeStyle = underlineNode && window.getComputedStyle(underlineNode).textDecoration;
      const underlined = nodeStyle && nodeStyle.includes(RteCommand.Underline);
      return {
        selectedTypography: {
          ...selectedTypography,
          bold: buttonClick ? boldState : Boolean(boldNode),
          italic: buttonClick ? italicState : Boolean(italicNode),
          underline: buttonClick ? underlineState : Boolean(underlined),
          orderedList: buttonClick ? olState : Boolean(orderedListNode),
          unorderedList: buttonClick ? ulState : Boolean(unorderedListNode)
        },
        selectedTypographicElement
      };
    });
  }
  getSelectedTypographicElement() {
    let selectedTypographicElement;
    if (this.getSelectedNodeOfType('p')) {
      selectedTypographicElement = TypographicElement.Paragraph;
    } else if (this.getSelectedNodeOfType('h1')) {
      selectedTypographicElement = TypographicElement.Heading1;
    } else if (this.getSelectedNodeOfType('h2')) {
      selectedTypographicElement = TypographicElement.Heading2;
    } else if (this.getSelectedNodeOfType('h3')) {
      selectedTypographicElement = TypographicElement.Heading3;
    } else if (this.getSelectedNodeOfType('h4')) {
      selectedTypographicElement = TypographicElement.Heading4;
    } else if (this.getSelectedNodeOfType('h5')) {
      selectedTypographicElement = TypographicElement.Heading5;
    } else if (this.getSelectedNodeOfType('h6')) {
      selectedTypographicElement = TypographicElement.Heading6;
    } else if (this.getSelectedNodeOfType('blockquote')) {
      selectedTypographicElement = TypographicElement.Blockquote;
    }
    return selectedTypographicElement;
  }
  handleDoubleClick() {
    if (this.state.imageSelected) {
      this.setState({
        modalVisible: RteModal.EditImage
      });
    }
  }
  handleRemoveImage() {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    const imageElement = this.getSelectedNodeOfType('IMG');
    editor.dom.remove(imageElement, true);
    this.getCommandExecutor('mceRepaint')();
    editor.selection.collapse();
    this.closeModal();
  }
  handleEditImage(newUrl, newAltText, newAlignment, isWrapText, newSpacing) {
    const {
      editor
    } = this;
    if (!editor) {
      return;
    }
    const imageElement = this.getSelectedNodeOfType('IMG');
    const divElement = this.getSelectedNodeOfType('div');
    const floatStyle = isWrapText ? `float: ${newAlignment};` : 'float: none;';
    let marginStyle = '';
    if (Object.values(newSpacing).includes('0') || Object.values(newSpacing).includes('')) {
      for (const spacing in newSpacing) {
        if (newSpacing[spacing] !== '' && newSpacing[spacing] !== '0') {
          marginStyle += `margin-${spacing}: ${newSpacing[spacing]}px; `;
        }
      }
    } else {
      marginStyle = `margin: ${newSpacing.top}px ${newSpacing.right}px ${newSpacing.bottom}px ${newSpacing.left}px;`;
    }
    const style = marginStyle + floatStyle;
    if (divElement) {
      editor.dom.setAttribs(divElement, {
        style: `text-align: ${newAlignment};`
      });
      editor.dom.setAttribs(imageElement, {
        src: newUrl,
        alt: newAltText,
        style
      });
    } else {
      editor.dom.remove(imageElement, true);
      const img = editor.dom.createHTML('img', {
        src: newUrl,
        alt: newAltText,
        style
      });
      const html = editor.dom.createHTML('div', {
        style: `text-align: ${newAlignment};`
      }, img);
      this.handleExecuteCommand('mceInsertContent', html, {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        skip_undo: 1
      });
    }
    editor.fire('change');
    this.getCommandExecutor('mceRepaint')();
    editor.selection.collapse();
    this.closeModal();
  }
}, (_applyDecoratedDescriptor(_class.prototype, "handleTextareaValueChanged", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleTextareaValueChanged"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleCodeMirrorChanged", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleCodeMirrorChanged"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleEditorValueChanged", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleEditorValueChanged"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "initializeTinyMCE", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "initializeTinyMCE"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setInitialEditorValue", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "setInitialEditorValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "sanitizeContent", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "sanitizeContent"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unsanitizeContent", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "unsanitizeContent"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "tinymceTheme", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "tinymceTheme"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleTinyMceLoaded", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleTinyMceLoaded"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleCodeMirrorLoaded", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleCodeMirrorLoaded"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setEditorVisibility", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "setEditorVisibility"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "displayLegacyTextarea", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "displayLegacyTextarea"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "getCommandExecutor", [autobind, _dec], Object.getOwnPropertyDescriptor(_class.prototype, "getCommandExecutor"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleExecuteCommand", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleExecuteCommand"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleHTML", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "toggleHTML"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleInsertImage", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleInsertImage"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleInsertVideo", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleInsertVideo"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleSelectionChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleSelectionChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleEditorFocus", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleEditorFocus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleEditorBlur", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleEditorBlur"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleOpenPopover", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleOpenPopover"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleClosePopover", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleClosePopover"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleCloseOverflow", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleCloseOverflow"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleToggleOverflow", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleToggleOverflow"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "getSelectedNodeOfType", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "getSelectedNodeOfType"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleUpdateLink", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleUpdateLink"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleRemoveLink", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleRemoveLink"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleOpenModal", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleOpenModal"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeModal", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "closeModal"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleClick", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleClick"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleColorChange", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleColorChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleColorState", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleColorState"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleNodeSelect", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleNodeSelect"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "getSelectedTypographicElement", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "getSelectedTypographicElement"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleDoubleClick", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleDoubleClick"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleRemoveImage", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleRemoveImage"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleEditImage", [autobind], Object.getOwnPropertyDescriptor(_class.prototype, "handleEditImage"), _class.prototype)), _class));
var RichTextEditor$1 = compose(withError(), withI18n({
  id: 'RichTextEditor_1njeino',
  fallback: _en,
  translations(locale) {
    if (["cs", "da", "de", "es", "fi", "fr", "it", "ja", "ko", "nb", "nl", "pl", "pt-BR", "pt-PT", "sv", "th", "tr", "vi", "zh-CN", "zh-TW"].indexOf(locale) < 0) {
      return;
    }
    return import( /* webpackChunkName: "RichTextEditor_1njeino-i18n", webpackMode: "lazy-once" */`./translations/${locale}.json`).then(dict => dict && dict.default);
  }
}))(RichTextEditor);
function parseRgb(rgbString) {
  const rgbRegex = /\d+/g;
  const rgbValues = rgbString.match(rgbRegex);
  if (rgbValues) {
    const [red, green, blue] = rgbValues.map(Number);
    return {
      red,
      green,
      blue
    };
  } else {
    return DEFAULT_BLACK;
  }
}
function calculateMargin(imageElementStyle) {
  let newSpacing = {
    top: '',
    left: '',
    bottom: '',
    right: ''
  };
  const margin = imageElementStyle.margin;
  if (margin) {
    const marginArr = margin.replace(/px/g, '').split(' ');
    switch (marginArr.length) {
      case 1:
        {
          newSpacing = {
            top: marginArr[0],
            right: marginArr[0],
            bottom: marginArr[0],
            left: marginArr[0]
          };
          break;
        }
      case 2:
        {
          newSpacing = {
            top: marginArr[0],
            right: marginArr[1],
            bottom: marginArr[0],
            left: marginArr[1]
          };
          break;
        }
      case 3:
        {
          newSpacing = {
            top: marginArr[0],
            right: marginArr[1],
            bottom: marginArr[2],
            left: marginArr[1]
          };
          break;
        }
      case 4:
        {
          newSpacing = {
            top: marginArr[0],
            right: marginArr[1],
            bottom: marginArr[2],
            left: marginArr[3]
          };
        }
    }
  } else {
    newSpacing = {
      top: imageElementStyle.marginTop == null ? '0' : imageElementStyle.marginTop,
      left: imageElementStyle.marginLeft == null ? '0' : imageElementStyle.marginLeft,
      bottom: imageElementStyle.marginBottom == null ? '0' : imageElementStyle.marginBottom,
      right: imageElementStyle.marginRight == null ? '0' : imageElementStyle.marginRight
    };
  }
  return newSpacing;
}

export { RichTextEditor$1 as default };
